import React, { useEffect } from 'react';

import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { StatusBar } from '@capacitor/status-bar';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter } from 'react-router-dom';

import {
  useAuthStore,
  ApiClientProvider,
  initGraphQLClient,
  requestMiddleware,
  AppRoutesProvider,
  IntercomProvider,
  IntercomBootFailureReporter,
  PasskeyPostLoginPrompt,
  ScannerProvider,
  UnifiedLoaderProvider,
  NativeStoreUpdateBanner,
  toIntercomBootSentryEvent,
} from '@liscio/common';
import { AdapterDateFns, LocalizationProvider, createTheme } from '@liscio/ui';

import AppRoutes from './AppRoutes';
import { selectNativeStoreUpdates } from './nativeStoreUpdates';
import { AxiosInterceptors } from 'components/AxiosInterceptor/AxiosInterceptor';
import { FirmPdfPreviewProvider } from 'components/DocumentPreview/FirmPdfPreviewProvider';
import { PushNotifications } from 'components/PushNotifications/PushNotifications';
import { config } from 'config';
import apiClient from 'fetch-utils/api-client';
import { DeviceClassProvider } from 'providers/DeviceClassProvider';
import LaunchDarklyProvider from 'providers/LaunchDarklyProvider';
import { PostHogProvider } from 'providers/PostHog';
import { WebsocketProvider } from 'providers/WebsocketProvider';
import WebsocketLauncher from 'providers/WebsocketProvider/WebsocketLauncher';
import { routes } from 'routes';
import { documentPreview } from 'theme/documentPreview';
import { refactorComponents } from 'theme/refactor-theme';

import './assets/fonts/fonts.css';

const liscioTheme = createTheme({
  extensions: [refactorComponents, documentPreview],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 15 * 1000,
      staleTime: 10 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const storeUpdates = selectNativeStoreUpdates(import.meta.env.MODE);

const reportIntercomBootFailure: IntercomBootFailureReporter = (failure) => {
  const { message, context } = toIntercomBootSentryEvent(failure);
  Sentry.captureMessage(message, context);
};

function App() {
  const authToken = useAuthStore(({ auth_token }) => auth_token);
  useEffect(() => {
    if (Capacitor.getPlatform() === 'android') {
      void SystemBars.setStyle({ style: SystemBarsStyle.Dark }).catch(
        Sentry.captureException
      );
      void Device.getInfo()
        .then(async ({ androidSDKVersion }) => {
          if (typeof androidSDKVersion !== 'number') {
            Sentry.addBreadcrumb({
              category: 'status-bar',
              level: 'warning',
              message: 'Android SDK version is unavailable',
            });
            return;
          }
          if (androidSDKVersion < 35) {
            await StatusBar.setOverlaysWebView({ overlay: false });
            await StatusBar.setBackgroundColor({
              color: config.statusBar.backgroundColor,
            });
          }
        })
        .catch(Sentry.captureException);
      //eslint-disable-next-line
      console.info('Liscio app');
    }
  }, []);

  const graphQLClient = initGraphQLClient(config.api.url, requestMiddleware);
  const appContent = (
    <BrowserRouter>
      <IntercomProvider
        workspace="firm"
        onBootFailure={reportIntercomBootFailure}
      >
        <AxiosInterceptors />
        <CssBaseline />
        <Helmet title="Dashboard" />
        <PushNotifications />
        <NativeStoreUpdateBanner
          updates={storeUpdates}
          onLookupError={Sentry.captureException}
        />
        <PasskeyPostLoginPrompt />
        {authToken && <WebsocketLauncher authToken={authToken} />}
        <Toaster
          position="top-center"
          containerStyle={{
            marginTop:
              'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          }}
          // A long file name has no spaces to wrap at: break inside it so it stays in the toast.
          toastOptions={{ style: { overflowWrap: 'anywhere' } }}
        />
        <FirmPdfPreviewProvider>
          <AppRoutes />
        </FirmPdfPreviewProvider>
      </IntercomProvider>
    </BrowserRouter>
  );

  return (
    <DeviceClassProvider>
      <HelmetProvider>
        <LaunchDarklyProvider>
          <WebsocketProvider>
            <ThemeProvider theme={liscioTheme}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <QueryClientProvider client={queryClient}>
                  <ApiClientProvider
                    apiClient={apiClient}
                    graphQLClient={graphQLClient}
                  >
                    <PostHogProvider>
                      <UnifiedLoaderProvider>
                        <AppRoutesProvider {...routes}>
                          {config.scanbot.isEnabled ? (
                            <ScannerProvider
                              onError={(error, details) =>
                                Sentry.captureException(error, {
                                  extra: details,
                                })
                              }
                              onLicenseInfo={(info) =>
                                Sentry.addBreadcrumb({
                                  category: 'scanbot.license',
                                  level: 'info',
                                  data: info,
                                })
                              }
                              showLicenseStatus={
                                import.meta.env.MODE === 'native-uat'
                              }
                            >
                              {appContent}
                            </ScannerProvider>
                          ) : (
                            appContent
                          )}
                        </AppRoutesProvider>
                      </UnifiedLoaderProvider>
                    </PostHogProvider>
                  </ApiClientProvider>
                </QueryClientProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </WebsocketProvider>
        </LaunchDarklyProvider>
      </HelmetProvider>
    </DeviceClassProvider>
  );
}

export default App;
